
/**
 * Raw backend shape accepted by resolveLabeledCode.
 * Both fields are always present on the wire but either may be null.
 * The normalized LabeledCode type (code: string) is a structural subtype,
 * so LabeledCode values are accepted wherever LabeledCodeInput is expected.
 */
export type LabeledCodeInput = { code: string | null; libelle: string | null };

/**
 * Translates a backend dictionary code into its i18n label.
 * Falls back to the raw code when no translation is found, so unknown
 * values are always visible rather than silently blank.
 *
 * `t` is typed as the minimal interface this function actually needs:
 * a callable that accepts a key and an optional options object with `defaultValue`.
 * The optional options keep this compatible with simple `(k: string) => string`
 * callers (e.g. resolveFields, resolveEmployeeFields, test doubles) while still
 * accepting the full i18next `TFunction` signature.
 *
 * @param t       - i18n translation function (compatible with react-i18next `t`)
 * @param prefix  - dot-separated i18n key prefix (e.g. "employeeTables.filterOptions.gender")
 * @param code    - raw backend code (e.g. "CE", "F", "CDD") — null/undefined returns ""
 *
 * @example
 *   translateCode(t, 'employeeTables.filterOptions.maritalStatus', 'CE')
 *   // → "Célibataire"  (fr)  or  "Single"  (en)
 *
 *   translateCode(t, 'employeeTables.filterOptions.maritalStatus', 'UNKNOWN')
 *   // → "UNKNOWN"  (fallback to the raw code)
 *
 *   translateCode(t, 'employeeTables.filterOptions.gender', null)
 *   // → ""
 */
export function translateCode(
  t: (key: string, options?: { defaultValue: string }) => string,
  prefix: string,
  code: string | null | undefined,
): string {
  if (code == null || code === '') return '';
  return t(`${prefix}.${code}`, { defaultValue: code });
}

/**
 * Resolves a DICO field value for display using the 3-level priority:
 *   1. Backend libelle (if non-empty) — most specific, shown as-is
 *   2. i18n translation of the code (falls back to the raw code when missing)
 *   3. `${prefix}.emptyOrNull` i18n key — lets each dict define a translated
 *      empty-state string (e.g. "Non renseigné"); defaults to "" when absent
 *
 * Handles both LabeledCode objects (when backend has upgraded) and plain
 * strings (backward-compatible with the legacy backend that sends raw codes).
 */
export function resolveLabeledCode(
  t: (key: string, options?: { defaultValue: string }) => string,
  prefix: string,
  value: LabeledCodeInput | string | null | undefined,
): string {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value.libelle?.trim()) return value.libelle.trim();
    if (value.code) return translateCode(t, prefix, value.code);
    // Object present but both code and libelle are empty — backend explicitly sent no value
    return prefix ? t(`${prefix}.emptyOrNull`, { defaultValue: '' }) : '';
  }
  return translateCode(t, prefix, value);
}
